/**
 * Interesse quote_all API — request/response shapes from broker integration.
 */

/** Root POST body for quote_all. */
export interface InteresseQuoteRequest {
  quote: {
    operation_id: string;
    coverage_amount: number;
    vehicle_id: number;
    term_id: number;
    model: number;
    person_type_id: string;
    new_id: string;
    gender_id: string;
    tax_type_id: string;
    postal_code: string;
    vehicle_use_id: string;
    cargo_type_id: string;
  };
}

/** One insurer option in `data.Quotes`. */
export interface InteresseQuoteApiQuote {
  NetBonus: number;
  Surcharge: number;
  Rights: number;
  Tax: number;
  TotalBonus: number;
  Insurer: string;
  Package: string;
  InsuranceQuoteId: number;
  Comments: string | null;
  Quoteurl: string;
  Gap: number;
  GapNetBonus: number;
  GapTax: number;
  LifeInsurance: number;
  PackageId: string;
  InsuranceTypeId: string;
  VehicleKey: number;
  CreditNumber: number;
  InsuranceQuoteIdRef: number;
}

/** Row in `data.Receipts` (linked by InsuranceQuoteId). */
export interface InteresseQuoteApiReceipt {
  InsuranceQuoteId: number;
  Number: number;
  SinceDate: string;
  UntilDate: string;
  NetBonus: number;
  Rights: number;
  Tax: number;
  Surcharge: number;
  TotalBonus: number;
}

/** `data` object on a successful quote_all response. */
export interface InteresseQuoteDataEnvelope {
  Quotes: InteresseQuoteApiQuote[];
  Receipts: InteresseQuoteApiReceipt[];
}

/**
 * One quotable option for the UI: API quote row + its receipts (matched by InsuranceQuoteId).
 * Stored on BrokerQuote.complete_response_interesse.
 */
export interface InteresseQuoteResponseItem {
  quote: InteresseQuoteApiQuote;
  receipts: InteresseQuoteApiReceipt[];
}

/** Root body for POST `/insurance/emit_policy`. */
export interface InteresseEmitPolicyRequestEnvelope {
  readonly emit: InteresseEmitPolicyPayload;
}

export interface InteresseEmitAddress {
  readonly street: string;
  readonly externalNumber: string;
  readonly internalNumber: string;
  readonly suburb: string;
  readonly zipcode: string;
  readonly stateId: string;
}

export interface InteresseEmitBusiness {
  readonly commercialName: string;
  readonly legalName: string;
  readonly agencyNumber: string;
}

export interface InteresseEmitFiscalAddress {
  readonly street: string;
  readonly externalNumber: string;
  readonly internalNumber: string;
  readonly suburb: string;
  readonly municipality: string;
  readonly zipcode: string;
  readonly stateId: string;
}

export interface InteresseEmitFiscal {
  readonly rfc: string;
  readonly address?: InteresseEmitFiscalAddress;
}

export interface InteresseEmitPolicyPayload {
  readonly person: InteresseEmitPerson;
  readonly contact: InteresseEmitContact;
  readonly address: InteresseEmitAddress;
  readonly insurance: InteresseEmitInsurance;
  readonly vehicle: InteresseEmitVehicle;
  readonly business?: InteresseEmitBusiness;
  readonly fiscal?: InteresseEmitFiscal;
}

export interface InteresseEmitPerson {
  readonly type_id: "FISICA" | "MORAL";
  readonly given_name: string;
  readonly last_name: string;
  readonly second_last_name: string;
  readonly full_name: string;
  readonly gender_id: "MALE" | "FEMALE" | "NO_APLICA";
  readonly birth_date: string;
}

export interface InteresseEmitContact {
  readonly email: string;
  readonly cellphone: string;
}

export interface InteresseEmitInsurance {
  readonly quote_id: string;
  readonly type_id: string;
  readonly insurer: { readonly name: string; readonly id: string };
  readonly package: { readonly name: string; readonly id: string };
  readonly term: string;
  readonly until_date: string;
}

export interface InteresseEmitVehicle {
  readonly plates: string;
  readonly series: string;
  readonly engine: string;
}

/** Successful `data` on emit_policy (field names match API). */
export interface InteresseEmitPolicyData {
  readonly Policy: string;
  readonly URL: string;
  readonly SinceDate: string;
  readonly UntilDate: string;
}

export interface InteresseEmitPolicyApiResponse {
  readonly status: string;
  readonly data: InteresseEmitPolicyData | null;
  readonly errors?: readonly string[];
}
